import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const About = () => {
  const values = [
    {
      icon: '🎯',
      title: 'Excellence',
      description: 'Committed to delivering the highest quality commodities and services through rigorous quality control and supplier verification.',
    },
    {
      icon: '🤝',
      title: 'Integrity',
      description: 'Building lasting trust through transparent business practices, ethical trading relationships, and honest communication.',
    },
    {
      icon: '🌍',
      title: 'Global Perspective',
      description: 'Understanding diverse markets and cultures to serve clients across Europe, Asia, and Africa effectively.',
    },
    {
      icon: '⚡',
      title: 'Agility',
      description: 'Responding quickly to market changes and client needs with flexible, innovative solutions.',
    },
  ];

  const expertise = [
    {
      title: 'Commodity Sourcing',
      description: 'Access to reliable suppliers and producers across global markets, ensuring consistent quality and competitive pricing.',
      icon: '🔍',
      stat: '200+ Verified Suppliers',
    },
    {
      title: 'Supply Chain Management',
      description: 'End-to-end logistics coordination from origin to destination, with real-time tracking and proactive problem-solving.',
      icon: '📦',
      stat: '98% On-Time Delivery',
    },
    {
      title: 'Quality Assurance',
      description: 'Rigorous quality control processes and independent testing to meet and exceed international standards.',
      icon: '✓',
      stat: '100% Quality Checked',
    },
    {
      title: 'Market Intelligence',
      description: 'Deep understanding of commodity markets, pricing trends, and regulatory requirements across regions.',
      icon: '📊',
      stat: 'Real-Time Insights',
    },
    {
      title: 'Risk Management',
      description: 'Comprehensive risk assessment and mitigation strategies to protect your investments and ensure smooth transactions.',
      icon: '🛡️',
      stat: 'Full Risk Coverage',
    },
    {
      title: 'Custom Solutions',
      description: 'Tailored commodity sourcing and supply solutions based on specific client requirements and industry needs.',
      icon: '⚙️',
      stat: 'Bespoke Service',
    },
  ];

  const approach = [
    {
      step: '01',
      title: 'Understanding Your Needs',
      description: 'We begin by thoroughly understanding your commodity requirements, specifications, delivery timelines, and business objectives.',
      time: 'Day 1-2',
    },
    {
      step: '02',
      title: 'Sourcing & Verification',
      description: 'Leveraging our global network to source the right commodities and verify quality through independent testing and supplier audits.',
      time: 'Day 3-5',
    },
    {
      step: '03',
      title: 'Logistics Coordination',
      description: 'Managing all aspects of shipping, customs clearance, documentation, and delivery to ensure smooth operations.',
      time: 'Day 6-10',
    },
    {
      step: '04',
      title: 'Ongoing Support',
      description: 'Providing continuous support and transparency throughout the entire supply chain process and beyond delivery.',
      time: 'Continuous',
    },
  ];

  const timeline = [
    {
      year: '2014',
      title: 'Company Founded',
      description: 'Markhor Group established in London with a vision to provide reliable commodity trading services.',
    },
    {
      year: '2016',
      title: 'Expansion to Asia',
      description: 'Opened strategic partnerships in Asia, expanding our sourcing capabilities and market reach.',
    },
    {
      year: '2018',
      title: 'African Markets',
      description: 'Established supply networks across African markets, supporting regional development and trade.',
    },
    {
      year: '2020',
      title: 'Digital Transformation',
      description: 'Implemented advanced tracking and documentation systems for enhanced transparency and efficiency.',
    },
    {
      year: '2023',
      title: 'Sustainability Focus',
      description: 'Launched sustainable sourcing initiatives and partnerships with environmentally responsible suppliers.',
    },
    {
      year: '2024',
      title: 'Record Growth',
      description: 'Achieved record trading volumes handling over 100,000 tonnes annually across multiple commodities.',
    },
  ];

  const certifications = [
    { name: 'ISO 9001:2015', description: 'Quality Management' },
    { name: 'ISO 14001', description: 'Environmental Management' },
    { name: 'GAFTA Member', description: 'Grain & Feed Trade' },
    { name: 'FCStone Partner', description: 'Risk Management' },
  ];

  const team = [
    {
      role: 'Trading & Operations',
      description: 'Experienced commodity traders with deep market knowledge and established supplier networks.',
      count: '8+ Specialists',
    },
    {
      role: 'Logistics & Supply Chain',
      description: 'Expert logistics coordinators managing end-to-end supply chain operations.',
      count: '6+ Professionals',
    },
    {
      role: 'Quality & Compliance',
      description: 'Dedicated quality assurance team ensuring all shipments meet specifications.',
      count: '4+ Experts',
    },
    {
      role: 'Business Development',
      description: 'Building relationships and expanding our global network of clients and partners.',
      count: '5+ Managers',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary via-primary-dark to-accent text-white pt-32 pb-20 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <motion.div
            animate={{
              backgroundPosition: ['0px 0px', '100px 100px'],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear',
            }}
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Markhor Group</h1>
            <p className="text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Your trusted partner in global commodity trading and logistics solutions. 
              Based in London, serving clients across three continents with professionalism, 
              integrity, and deep market expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-primary mb-6">
                Commodity Trading Specialists with a <span className="text-secondary">Straightforward Approach</span>
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Markhor Group is a dynamic commodity trading company based in London, specializing in the supply of essential commodities across Europe, Asia, and Africa. We focus on <strong>Fertilisers (Potash MOP)</strong>, <strong>Thermal Coal</strong>, <strong>Wheat</strong>, and <strong>Metals including Titanium</strong>.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We understand that successful commodity trading requires more than just connecting buyers and sellers. It demands deep market knowledge, reliable partnerships, robust logistics capabilities, and an unwavering commitment to quality and service excellence.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our team brings together decades of combined experience in international trade, supply chain management, and commodity markets. We keep it straightforward—no unnecessary complications, just professional service that delivers results.
              </p>

              {/* Key Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-6 rounded-xl border border-secondary/20">
                  <div className="text-3xl font-bold text-primary mb-1">100K+</div>
                  <div className="text-sm text-gray-600">Tonnes Annually</div>
                </div>
                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-6 rounded-xl border border-secondary/20">
                  <div className="text-3xl font-bold text-primary mb-1">25+</div>
                  <div className="text-sm text-gray-600">Countries Served</div>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-block bg-secondary hover:bg-secondary-dark text-primary font-bold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Partner With Us
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Info Cards Stack */}
              <div className="space-y-6">
                <motion.div
                  whileHover={{ x: 10 }}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 border border-gray-100"
                >
                  <div className="flex items-start">
                    <div className="text-4xl mr-4">🏢</div>
                    <div>
                      <h4 className="font-bold text-primary text-xl mb-2">Headquarters</h4>
                      <p className="text-gray-700">London, United Kingdom</p>
                      <p className="text-sm text-gray-600 mt-1">Strategic location for global trade</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 10 }}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 border border-gray-100"
                >
                  <div className="flex items-start">
                    <div className="text-4xl mr-4">🌍</div>
                    <div>
                      <h4 className="font-bold text-primary text-xl mb-2">Markets Served</h4>
                      <p className="text-gray-700">Europe, Asia, Africa</p>
                      <p className="text-sm text-gray-600 mt-1">Three-continent coverage</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 10 }}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 border border-gray-100"
                >
                  <div className="flex items-start">
                    <div className="text-4xl mr-4">📦</div>
                    <div>
                      <h4 className="font-bold text-primary text-xl mb-2">Core Commodities</h4>
                      <p className="text-gray-700">Fertiliser, Thermal Coal, Wheat, Metals</p>
                      <p className="text-sm text-gray-600 mt-1">Plus custom sourcing on demand</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 10 }}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 border border-gray-100"
                >
                  <div className="flex items-start">
                    <div className="text-4xl mr-4">🚢</div>
                    <div>
                      <h4 className="font-bold text-primary text-xl mb-2">Full Services</h4>
                      <p className="text-gray-700">Trading, Sourcing, Logistics, QA</p>
                      <p className="text-sm text-gray-600 mt-1">Comprehensive support included</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide every decision we make
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <motion.div 
                  className="text-6xl mb-4"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {value.icon}
                </motion.div>
                <h3 className="text-2xl font-bold text-primary mb-3">{value.title}</h3>
                <p className="text-gray-700 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">What We Do Best</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive capabilities in commodity trading and supply chain management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertise.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-bold text-primary">{item.title}</h3>
                  <span className="text-xs bg-secondary/10 text-secondary px-3 py-1 rounded-full font-semibold whitespace-nowrap ml-2">
                    {item.stat}
                  </span>
                </div>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-gradient-to-br from-primary to-accent text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our Proven Process</h2>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              A systematic, transparent approach to every transaction
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approach.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center text-2xl font-bold text-primary mb-4 shadow-xl">
                  {item.step}
                </div>
                <div className="mb-2">
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{item.time}</span>
                </div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-200 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey/Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">Our Growth Journey</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Building trust and expanding capabilities year after year
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-secondary to-accent hidden md:block" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className={`flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right' : ''}`}>
                    <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg border border-gray-100">
                      <div className="text-secondary font-bold text-xl mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-primary mb-2">{item.title}</h3>
                      <p className="text-gray-700">{item.description}</p>
                    </div>
                  </div>

                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-xl my-4 md:my-0 md:mx-4 z-10">
                    {index + 1}
                  </div>

                  <div className="w-full md:w-5/12" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Partnerships */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">Quality & Compliance</h2>
            <p className="text-xl text-gray-600">
              Certified and trusted across the industry
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white p-8 rounded-xl shadow-lg text-center border-2 border-secondary/20 hover:border-secondary/50 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-2xl text-white mb-4 mx-auto">
                  ✓
                </div>
                <h3 className="text-lg font-bold text-primary mb-2">{cert.name}</h3>
                <p className="text-sm text-gray-600">{cert.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Structure */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-primary mb-4">Expert Team</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Dedicated professionals with deep industry experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {team.map((dept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-primary/5 to-secondary/5 p-8 rounded-2xl border border-secondary/20"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-2xl font-bold text-primary">{dept.role}</h3>
                  <span className="text-secondary font-bold text-lg">{dept.count}</span>
                </div>
                <p className="text-gray-700 leading-relaxed">{dept.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Stats */}
      <section className="py-24 bg-gradient-to-r from-primary to-accent text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Why Choose Markhor Group?</h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Clean, professional, trustworthy—a company that really knows the export game but keeps it straightforward.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8 mt-12">
            {[
              { number: '3+', label: 'Years Experience', sublabel: 'Combined team expertise' },
              { number: '3', label: 'Continents', sublabel: 'Europe, Asia, Africa' },
              { number: '98%', label: 'Client Retention', sublabel: 'Long-term partnerships' },
              { number: '24/7', label: 'Support', sublabel: 'Always here for you' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/20"
              >
                <h3 className="text-6xl font-bold text-secondary mb-2">{stat.number}</h3>
                <p className="text-xl font-semibold mb-1">{stat.label}</p>
                <p className="text-gray-300 text-sm">{stat.sublabel}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-4xl font-bold text-primary mb-6">
            Let's Build a Partnership
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Discover how our expertise, straightforward approach, and global network can support your commodity requirements. Get in touch today for a consultation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="bg-secondary hover:bg-secondary-dark text-primary font-bold px-12 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Get in Touch
            </Link>
            <Link
              to="/products"
              className="border-2 border-primary hover:bg-primary hover:text-white text-primary font-semibold px-12 py-4 rounded-lg transition-all duration-300"
            >
              View Our Commodities
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;